import { AuthProvider, LoginRequest, PlayerResponse } from "./dto/auth.dto.js";
import type { PlayerDto } from "./dto/player.dto.js";
import { GlobalSettings } from "../global_settings.js";
import { login } from "./requests/auth.requests.js";
import { ApiError } from "./http_client.js";
import { AuthSessionStore } from "./auth_session.js";
import { ensureDeviceId } from "../utils/device_id.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { OfferService } from "../offers/offer_service.js";
import { syncUserSourceAfterLogin } from "./user_source_sync.js";
import { syncPlayerAfterLogin, syncProfileFromServer } from "./player_sync.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { OfflineSyncService } from "../offline/offline_sync_service.js";
import { getPlayer } from "./requests/player.requests.js";
import { initLinkedProvidersFromServer, resetLinkedSocialProviders } from "../ui/dialog/account_providers_preview_state.js";
import { GoogleSignInPlugin } from "../plugins/play_games_plugin.js";
import { FacebookSignInPlugin } from "../plugins/facebook_sign_in_plugin.js";
import { applePlugin } from "./account_link_service.js";

export interface LoginOptions
{
  force_guest?: boolean;
}

async function cachedProviderId( read: () => Promise<string | null> ): Promise<string | null>
{
  try
  {
    const id = await read();
    return id && id.trim() ? id : null;
  }
  catch
  {
    return null;
  }
}

async function resolveLinkedProvider( device_id: string ): Promise<LoginRequest | null>
{
  const google_id = await cachedProviderId( () => GoogleSignInPlugin.getCachedId() );
  if ( google_id ) return { provider: AuthProvider.Google, provider_id: google_id, device_id };

  const facebook_id = await cachedProviderId( () => FacebookSignInPlugin.getCachedId() );
  if ( facebook_id ) return { provider: AuthProvider.Facebook, provider_id: facebook_id, device_id };

  const apple_id = await cachedProviderId( () => applePlugin().getCachedId() );
  if ( apple_id ) return { provider: AuthProvider.Apple, provider_id: apple_id, device_id };

  return null;
}

function isSocialProvider( provider: AuthProvider ): boolean
{
  return provider === AuthProvider.Google || provider === AuthProvider.Facebook || provider === AuthProvider.Apple;
}

async function resolveProvider( options: LoginOptions ): Promise<LoginRequest>
{
  const device_id = await ensureDeviceId();

  if ( !options.force_guest )
  {
    const linked = await resolveLinkedProvider( device_id );
    if ( linked ) return linked;
  }

  const telegram_id = TelegramAPISettings.getUserId();
  if ( telegram_id )
  {
    return { provider: AuthProvider.Telegram, provider_id: telegram_id, device_id };
  }

  return { provider: AuthProvider.Guest, provider_id: device_id, device_id };
}

export async function performLogin( options: LoginOptions = {} ): Promise<boolean>
{
  try
  {
    let request = await resolveProvider( options );
    let player: PlayerResponse;
    try
    {
      player = await login( request, isSocialProvider( request.provider ) ? AuthSessionStore.getToken() : null );
    }
    catch ( e )
    {
      if ( !( e instanceof ApiError && e.status === 401 && isSocialProvider( request.provider ) ) ) throw e;
      console.warn( `[Login] ${ request.provider } session rejected by server, continuing as guest` );
      request = await resolveProvider( { force_guest: true } );
      player = await login( request );
    }
    AuthSessionStore.setFromPlayer( player );
    resetLinkedSocialProviders();
    initLinkedProvidersFromServer( player.linked_providers ?? [] );
    if ( OfflineJournal.hasPending() && !OfflineSyncService.isReplaying() )
    {
      const stats = await OfflineSyncService.replayPending();
      console.info( `[Login] offline journal replayed: ${stats.replayed} ok, ${stats.dropped} dropped${stats.stopped ? ", stopped" : ""}` );
      if ( stats.replayed > 0 )
        player = { ...( await getPlayer() ), token: player.token } as PlayerResponse;
    }
    if ( OfflineJournal.hasPending() )
    {
      console.warn( '[Login] offline journal still pending — keeping local progress until the next sync' );
      syncProfileFromServer( player as unknown as PlayerDto );
      OfflineEconomy.restoreInventoryFromStats();
    }
    else
      await syncPlayerAfterLogin( player as unknown as PlayerDto );
    if ( GlobalSettings.APPSFLYER_ATTRIBUTION_ENABLED )
      void syncUserSourceAfterLogin( player.id );

    if ( GlobalSettings.OFFERS_ENABLED )
    {
      await OfferService.refresh();
      await OfferService.restorePurchases();
    }

    console.log( `[Login] Logged in as ${request.provider}, player id ${player.id}` );
    return true;
  }
  catch ( e )
  {
    console.warn( '[Login] Failed, continuing offline', e );
    return false;
  }
}
